import { useCallback, useEffect, useState, useMemo } from 'react';
import { ITimelineEvent } from '@/types/timeline.ts';
import { useServerConnection } from '@/hooks/debugServerConnection';
import { useNetworkSimulation } from '@/hooks/useNetworkSimulation';
import { demoDataGenerator } from '@/utils/demoDataGenerator';

interface IUseDebugTimelineMachineProps {
  events?: ITimelineEvent[]; // deprecated - 하위호환성을 위해 유지
  demoMode?: boolean; // 데모 모드 활성화
}

/**
 * Debug Time Machine의 메인 훅
 * 모든 디버깅 기능과 UI 상태를 통합 관리
 */
export function useDebugTimelineMachine(props?: IUseDebugTimelineMachineProps) {
  const { demoMode = false } = props || {};

  // === 서버 연결 (내부적으로 useServerConnection 사용) ===
  const {
    isConnected,
    connectedClients,
    events: serverEvents,
    connectionError,
    memoryStats,
    reconnect,
    clearEvents: clearServerEvents,
    updateEvents: updateServerEvents,
  } = useServerConnection();

  // === 데모 모드용 로컬 이벤트 상태 ===
  const [demoEvents, setDemoEvents] = useState<ITimelineEvent[]>([]);

  // === 네트워크 시뮬레이션 ===
  const {
    requests: networkRequests,
    isRecording: isNetworkRecording,
    toggleRecording: handleToggleNetworkRecording,
    clearHistory: handleClearNetworkHistory,
    exportData: handleExportNetworkData,
  } = useNetworkSimulation();

  // === UI 상태 관리 ===
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<ITimelineEvent | null>(null);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [timeJump, setTimeJump] = useState('');
  const [activeTab, setActiveTab] = useState<'timeline' | 'metrics' | 'components' | 'advanced'>('timeline');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // 우측 패널 토글 상태 추가
  const [stateInspectorOpen, setStateInspectorOpen] = useState(true);
  const [memoryMonitorOpen, setMemoryMonitorOpen] = useState(true);

  // === 데이터 계산 ===
  // 서버에서 데이터가 있으면 서버 데이터를 사용, 없으면 데모 데이터 사용
  const timelineEvents = serverEvents.length > 0 ? serverEvents : (demoMode ? demoEvents : []);

  // 디버그 로그 추가
  console.log('🔍 useDebugTimelineMachine Debug:', {
    demoMode,
    isConnected,
    serverEventsCount: serverEvents.length,
    demoEventsCount: demoEvents.length,
    timelineEventsCount: timelineEvents.length,
    connectionError,
    connectedClientsCount: connectedClients.length,
  });

  // 변화 감지용 useEffect 추가 (무한루프 방지)
  useEffect(() => {
    console.log('📊 Events updated:', {
      serverEvents: serverEvents.length,
      demoEvents: demoEvents.length,
      timeline: timelineEvents.length,
      isConnected,
    });
  }, [serverEvents.length, demoEvents.length, isConnected]); // timelineEvents.length 제거

  // 필터링된 이벤트 계산
  const filteredEvents = useMemo(() => {
    return timelineEvents.filter(event => {
      if (filter === 'errors' && !(event.isError || event.severity === 'critical')) return false;
      if (filter === 'api' && !(event.type === 'api_call' || event.type === 'API_CALL')) return false;
      if (filter === 'actions' && !(event.type === 'user_action' || event.type === 'action' || event.type === 'USER_ACTION')) return false;
      if (filter === 'states' && !(event.type === 'state_change' || event.type === 'STATE_CHANGE')) return false;

      return !(
        searchQuery &&
        !event.title.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !event.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    });
  }, [timelineEvents, filter, searchQuery]);

  // === 유틸리티 함수들 ===
  
  // 필터별 카운트 계산
  const getFilterCount = useCallback((type: string) => {
    switch (type) {
      case 'errors':
        return timelineEvents.filter(e => e.isError || e.severity === 'critical').length;
      case 'api':
        return timelineEvents.filter(e => e.type === 'api_call' || e.type === 'API_CALL').length;
      case 'actions':
        return timelineEvents.filter(e => e.type === 'user_action' || e.type === 'action' || e.type === 'USER_ACTION').length;
      case 'states':
        return timelineEvents.filter(e => e.type === 'state_change' || e.type === 'STATE_CHANGE').length;
      default:
        return timelineEvents.length;
    }
  }, [timelineEvents]);

  // 상태 컬러 계산
  const getStatusColor = useCallback(() => {
    if (!isConnected) return 'bg-red-500';
    if (timelineEvents.some(e => e.isError)) return 'bg-yellow-500';
    return 'bg-green-500';
  }, [isConnected, timelineEvents]);

  // === 이벤트 조작 함수들 ===

  // 북마크 토글
  const toggleBookmark = useCallback((eventId: string) => {
    const updatedEvents = timelineEvents.map(event =>
      event.id === eventId ? { ...event, isBookmarked: !event.isBookmarked } : event
    );
    
    if (demoMode) {
      setDemoEvents(updatedEvents);
    } else {
      updateServerEvents(updatedEvents);
    }
  }, [timelineEvents, demoMode, updateServerEvents]);

  // 시간 이동 관련 함수들
  const jumpToTime = useCallback((timestamp: number) => {
    setCurrentTime(timestamp);
    const event = timelineEvents.find(e => e.timestamp === timestamp);
    if (event) setSelectedEvent(event);
  }, [timelineEvents]);

  const jumpToPrevEvent = useCallback(() => {
    const currentIndex = timelineEvents.findIndex(e => e.timestamp === currentTime);
    if (currentIndex > 0) {
      jumpToTime(timelineEvents[currentIndex - 1].timestamp);
    }
  }, [timelineEvents, currentTime, jumpToTime]);

  const jumpToNextEvent = useCallback(() => {
    const currentIndex = timelineEvents.findIndex(e => e.timestamp === currentTime);
    if (currentIndex < timelineEvents.length - 1) {
      jumpToTime(timelineEvents[currentIndex + 1].timestamp);
    }
  }, [timelineEvents, currentTime, jumpToTime]);

  const handleTimeJump = useCallback(() => {
    const timeValue = parseInt(timeJump);
    if (!isNaN(timeValue) && timeValue >= 0 && timeValue <= timelineEvents.length) {
      setCurrentTime(timeValue);
      setTimeJump('');
    }
  }, [timeJump, timelineEvents.length]);

  const shareSnapshot = useCallback(async () => {
    const shareUrl = `${window.location.origin}/?time=${currentTime}&event=${selectedEvent?.id}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch (error) {
      console.warn('Failed to copy to clipboard:', error);
    }
  }, [currentTime, selectedEvent?.id]);

  // === 이펙트들 ===

  // 데모 모드 초기화 - 실제 서버 데이터 대기
  useEffect(() => {
    console.log('🔍 Frontend Dashboard initialized - waiting for real data from demo app');
    console.log('📊 Current state:', {
      demoMode,
      isConnected,
      serverEventsCount: serverEvents.length,
      connectedClientsCount: connectedClients.length,
    });
  }, [demoMode, isConnected, serverEvents.length, connectedClients.length]);

  // 새로운 이벤트가 추가되면 현재 시간을 최신으로 업데이트
  useEffect(() => {
    if (timelineEvents.length > 0) {
      const latestEvent = timelineEvents[timelineEvents.length - 1];
      setCurrentTime(latestEvent.timestamp);
    }
  }, [timelineEvents.length]); // timelineEvents 전체가 아닌 length만 의존성으로

  // 현재 시간에 해당하는 이벤트를 선택된 이벤트로 설정
  useEffect(() => {
    const currentEvent = timelineEvents.find(e => e.timestamp === currentTime);
    if (currentEvent && currentEvent.id !== selectedEvent?.id) {
      setSelectedEvent(currentEvent);
    }
  }, [currentTime, timelineEvents.length, selectedEvent?.id]); // timelineEvents 전체가 아닌 length만

  // === 반환 값 ===
  return {
    // 서버 연결 상태
    isConnected,
    connectionError,
    reconnect,
    connectedClients,
    memoryStats,

    // 이벤트 데이터
    timelineEvents,
    filteredEvents,

    // UI 상태
    currentTime,
    selectedEvent,
    filter,
    searchQuery,
    timeJump,
    isPlaying,
    activeTab,
    sidebarCollapsed,
    stateInspectorOpen,
    memoryMonitorOpen,

    // 네트워크 관련
    networkRequests,
    isNetworkRecording,

    // 액션 함수들
    jumpToTime,
    jumpToPrevEvent,
    jumpToNextEvent,
    handleTimeJump,
    shareSnapshot,
    toggleBookmark,
    
    // 상태 업데이트 함수들
    setFilter,
    setSearchQuery,
    setIsPlaying,
    setCurrentTime,
    setSelectedEvent,
    setTimeJump,
    setActiveTab,
    setSidebarCollapsed,
    setStateInspectorOpen,
    setMemoryMonitorOpen,

    // 네트워크 액션 함수들
    handleToggleNetworkRecording,
    handleClearNetworkHistory,
    handleExportNetworkData,

    // 메모리 관리 함수들
    updateEvents: demoMode ? setDemoEvents : updateServerEvents,
    clearEvents: demoMode ? () => setDemoEvents([]) : clearServerEvents,

    // 유틸리티 함수들
    getFilterCount,
    getStatusColor,
  };
}
